import type {OverrideOrDefaultData, CompRef, Pointer, PS} from '@wix/document-services-types'
import slots from '../../slots/slots'
import component from '../../component/component'
import constants from '../../constants/constants'
import {guidUtils} from '@wix/santa-core-utils'
import type {
    DataExtensionAPI,
    ManifestExtensionAPI,
    BuilderComponentsExtensionAPI
} from '@wix/document-manager-extensions'
import {DATA} from '@wix/component-protocol/schema'
import experiment from 'experiment-amd'
import {populateSlotWithSystemContainer} from './slotPopulationUtils'

type PropertyItemMap = Record<string, PropertyItem[]>

interface PropertyItem {
    id?: string
    data: {}
    type: string
}

function shouldProcessContainerKey(arrayItemDataManifest: OverrideOrDefaultData, key: string): boolean {
    const itemManifest = arrayItemDataManifest.metaData.data.items[key]
    const containerDataType = itemManifest.dataType === DATA.DATA_TYPE.container

    if (!containerDataType || !itemManifest.container) {
        return false
    }

    const {containerType} = itemManifest.container
    return containerDataType && ['simple', 'template', 'placeholder'].includes(containerType)
}

const tryRemoveSystemContainerFromSlot = (ps: PS, slotsData: any, slotGuid: string): boolean => {
    //Once we add a validator check to ensure each GUID item exists in the slot as a key, this can be removed
    if (!slotsData?.slots?.[slotGuid]) {
        return false
    }

    const systemContainerId = slotsData.slots[slotGuid]
    const systemContainerPointer = ps.pointers.getPointer(systemContainerId, constants.VIEW_MODES.DESKTOP)

    component.deleteComponent(ps, systemContainerPointer as CompRef)

    delete slotsData.slots[slotGuid]

    return true
}

const getDataToUpdate = (
    manifest,
    componentRef: Pointer,
    includeInnerElements: boolean,
    propertyItemMap: PropertyItemMap
): OverrideOrDefaultData[] => {
    return manifest
        .getData(componentRef, includeInnerElements, [{prop: 'arrayItems'}])
        .filter(({prop}) => propertyItemMap[prop]?.length)
}

function findDuplicateSource(
    newItem: PropertyItem,
    existingPropertyItems: PropertyItem[],
    containerKeys: string[]
): PropertyItem | null {
    return existingPropertyItems.find(existingItem => {
        return containerKeys.some(
            key => existingItem.data[key] && newItem.data[key] && existingItem.data[key] === newItem.data[key]
        )
    })
}

function duplicateSystemContainer(
    ps: PS,
    componentRef: Pointer,
    sourceItem: PropertyItem,
    newItem: PropertyItem,
    containerKey: string,
    arrayItemDataManifest: OverrideOrDefaultData
) {
    const sourceSlotGuid = sourceItem.data[containerKey]
    const newSlotGuid = guidUtils.getGUID()
    newItem.data[containerKey] = newSlotGuid

    const innerElementPath = arrayItemDataManifest.path
    let sourceContainerId: string

    if (innerElementPath) {
        const slotsData = ps.publicAPI.innerElements.getInnerElementSlots(componentRef, innerElementPath)
        sourceContainerId = slotsData.slots[sourceSlotGuid]
    } else {
        const componentDef = ps.dal.get(componentRef)
        const slotsPointer = ps.pointers.getPointer(componentDef.slotsQuery, 'slots')
        const slotsData = ps.dal.get(slotsPointer)
        sourceContainerId = slotsData.slots[sourceSlotGuid]
    }

    if (sourceContainerId) {
        const sourceContainerPointer = ps.pointers.getPointer(
            sourceContainerId,
            constants.VIEW_MODES.DESKTOP
        ) as CompRef
        const duplicatedContainerRef = component.getComponentToDuplicateRef(
            ps,
            sourceContainerPointer,
            componentRef as CompRef
        )

        component.duplicate(ps, duplicatedContainerRef, sourceContainerPointer, componentRef as CompRef)

        if (innerElementPath) {
            ps.publicAPI.innerElements.setInnerElementSlot(
                componentRef,
                innerElementPath,
                newSlotGuid,
                duplicatedContainerRef.id
            )
        } else {
            const componentDef = ps.dal.get(componentRef)
            const slotsPointer = ps.pointers.getPointer(componentDef.slotsQuery, 'slots')
            const slotsData = ps.dal.get(slotsPointer)
            slotsData.slots[newSlotGuid] = duplicatedContainerRef.id
            ps.dal.set(slotsPointer, slotsData)
        }
    }
}

function processDuplicateItem(
    ps: PS,
    componentRef: Pointer,
    newItem: PropertyItem,
    sourceItem: PropertyItem,
    arrayItemDataManifest: OverrideOrDefaultData
) {
    const containerKeys = Object.keys(arrayItemDataManifest.metaData.data.items).filter(key =>
        shouldProcessContainerKey(arrayItemDataManifest, key)
    )

    containerKeys.forEach(key => {
        duplicateSystemContainer(ps, componentRef, sourceItem, newItem, key, arrayItemDataManifest)
    })
}

function processNewItem(
    ps: PS,
    componentRef: Pointer,
    newItem: PropertyItem,
    arrayItemDataManifest: OverrideOrDefaultData
) {
    Object.keys(arrayItemDataManifest.metaData.data.items).forEach(key => {
        if (!shouldProcessContainerKey(arrayItemDataManifest, key)) {
            return
        }

        const slotName = guidUtils.getGUID()
        newItem.data[key] = slotName
        populateSlotWithSystemContainer(ps, componentRef, slotName, arrayItemDataManifest.path)
    })
}

function processRemovedItems(
    ps: PS,
    componentRef: Pointer,
    existingPropertyItems: PropertyItem[],
    updatedPropertyItems: PropertyItem[],
    arrayItemDataManifest: OverrideOrDefaultData
) {
    const updatedItemIds = new Set(updatedPropertyItems.map(item => item.id).filter(Boolean))
    const removedItems = existingPropertyItems.filter(item => item.id && !updatedItemIds.has(item.id))

    removedItems.forEach(removedItem => {
        Object.keys(arrayItemDataManifest.metaData.data.items).forEach(key => {
            if (!shouldProcessContainerKey(arrayItemDataManifest, key)) {
                return
            }

            const slotGuid = removedItem.data?.[key]
            if (slotGuid) {
                const innerElementPath = arrayItemDataManifest.path
                if (innerElementPath) {
                    const slotData = ps.publicAPI.innerElements.getInnerElementSlots(componentRef, innerElementPath)
                    if (tryRemoveSystemContainerFromSlot(ps, slotData, slotGuid)) {
                        ps.publicAPI.innerElements.setInnerElementSlots(
                            slots.getComponentToAddPointer(ps, componentRef),
                            componentRef,
                            innerElementPath,
                            undefined,
                            undefined,
                            slotData
                        )
                    }
                } else {
                    const componentDef = ps.dal.get(componentRef)
                    if (componentDef.slotsQuery) {
                        const slotsPointer = ps.pointers.getPointer(componentDef.slotsQuery, 'slots')
                        const slotsData = ps.dal.get(slotsPointer)

                        if (tryRemoveSystemContainerFromSlot(ps, slotsData, slotGuid)) {
                            ps.dal.set(slotsPointer, slotsData)
                        }
                    }
                }
            }
        })
    })
}

function processNewOrDuplicateItems(
    ps: PS,
    componentRef: Pointer,
    existingPropertyItems: PropertyItem[],
    updatedPropertyItems: PropertyItem[],
    arrayItemDataManifest: OverrideOrDefaultData
) {
    const existingItemIds = new Set(existingPropertyItems.map(item => item.id).filter(Boolean))
    const newItems = updatedPropertyItems.filter(item => {
        if (!item.id) {
            return true
        }
        // A situation when new item is sent with id
        return !existingItemIds.has(item.id)
    })

    const containerKeys = Object.keys(arrayItemDataManifest.metaData.data.items).filter(key =>
        shouldProcessContainerKey(arrayItemDataManifest, key)
    )

    newItems.forEach(newItem => {
        const duplicateSource = findDuplicateSource(newItem, existingPropertyItems, containerKeys)

        if (duplicateSource) {
            processDuplicateItem(ps, componentRef, newItem, duplicateSource, arrayItemDataManifest)
        } else {
            processNewItem(ps, componentRef, newItem, arrayItemDataManifest)
        }
    })
}

const handleElementDataUpdate = (
    ps: PS,
    componentRef: CompRef,
    propertyItemMap: PropertyItemMap,
    arrayItemsListIncludeInnerElement: OverrideOrDefaultData[]
) => {
    arrayItemsListIncludeInnerElement.forEach((arrayItemDataManifest: OverrideOrDefaultData) => {
        const propertyItems = propertyItemMap[arrayItemDataManifest.prop]
        const {data} = ps.extensionAPI as DataExtensionAPI
        const existingPropertyItems = data.get(componentRef)?.data?.[arrayItemDataManifest.prop] || []

        processRemovedItems(ps, componentRef, existingPropertyItems, propertyItems, arrayItemDataManifest)

        processNewOrDuplicateItems(ps, componentRef, existingPropertyItems, propertyItems, arrayItemDataManifest)
    })
}

const validateNoDuplicateIds = (data: any) => {
    const seenIds = new Set<string>()
    const duplicateIds = new Set<string>()

    const collectIds = (items: any[]) => {
        items.forEach(item => {
            if (item.id) {
                if (seenIds.has(item.id)) {
                    duplicateIds.add(item.id)
                } else {
                    seenIds.add(item.id)
                }
            }
        })
    }

    Object.values(data).forEach(value => {
        if (Array.isArray(value)) {
            collectIds(value)
        }
    })

    if (duplicateIds.size > 0) {
        const duplicateIdsList = Array.from(duplicateIds).join(', ')
        throw new Error(`Duplicate _id values found in update data: ${duplicateIdsList}`)
    }
}

const builderComponentsDataUpdateHook = (ps: PS, componentRef: CompRef, updatedDataItem: any) => {
    if (!experiment.isOpen('dm_autoPopulateSlotToContainer')) {
        return
    }

    const {manifest} = ps.extensionAPI as ManifestExtensionAPI
    const {builderComponents} = ps.extensionAPI as BuilderComponentsExtensionAPI

    const componentDefinition = ps.dal.get(componentRef)
    if (!builderComponents.isBuilder(componentDefinition.componentType)) return

    validateNoDuplicateIds(updatedDataItem.data)

    const arrayItemsListIncludeInnerElement = getDataToUpdate(manifest, componentRef, true, updatedDataItem.data)

    handleElementDataUpdate(ps, componentRef, updatedDataItem.data, arrayItemsListIncludeInnerElement)
}

export {builderComponentsDataUpdateHook}
